import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
    Container, Row, Col, Card, Button, ButtonGroup, Form, Modal,
    Table, Badge, Spinner, Alert
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { QRCodeCanvas } from 'qrcode.react';
import AuthContext from '../context/AuthContext';
import { getTables, createTable, bulkCreateTables, updateTable, deactivateTable, activateTable } from '../services/api';

// ─── QR Download Helper ────────────────────────────────────────────────────────

const downloadQR = (tableId, tableName) => {
    const canvas = document.getElementById(`qr-${tableId}`);
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `mesa-${tableName.replace(/\s+/g, '-')}.png`;
    a.click();
};

// ─── TableFormModal ───────────────────────────────────────────────────────────

// Genera los nombres de mesa a partir del prefijo, número inicial y cantidad.
const buildRangeNames = (prefix, startNumber, quantity) => {
    const start = Number.parseInt(startNumber, 10);
    const count = Number.parseInt(quantity, 10);
    if (!Number.isFinite(start) || !Number.isFinite(count) || count < 1) return [];
    const clean = (prefix || '').trim();
    return Array.from({ length: count }, (_, i) => `${clean} ${start + i}`.trim());
};

// Parsea el textarea de la lista: un nombre por línea, sin vacíos.
const parseListNames = (text) =>
    (text || '').split('\n').map(s => s.trim()).filter(Boolean);

const TableFormModal = ({ show, onHide, onSaved, editingTable }) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [mode, setMode] = useState('one');          // 'one' | 'many'
    const [bulkTab, setBulkTab] = useState('range');  // 'range' | 'list'
    const [prefix, setPrefix] = useState('');
    const [startNumber, setStartNumber] = useState(1);
    const [quantity, setQuantity] = useState(5);
    const [namesText, setNamesText] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (show) {
            setName(editingTable ? editingTable.name : '');
            setMode('one');
            setBulkTab('range');
            setPrefix('');
            setStartNumber(1);
            setQuantity(5);
            setNamesText('');
            setError('');
        }
    }, [show, editingTable]);

    const bulkNames = bulkTab === 'range'
        ? buildRangeNames(prefix, startNumber, quantity)
        : parseListNames(namesText);

    const previewNames = bulkNames.slice(0, 5).join(', ') + (bulkNames.length > 5 ? '…' : '');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!editingTable && mode === 'many') {
            if (bulkNames.length === 0) {
                setError(t('tablesPage.nameRequired'));
                return;
            }
            setSaving(true);
            try {
                const res = await bulkCreateTables({ names: bulkNames });
                onSaved(res.data, 'bulkCreated');
                onHide();
            } catch (err) {
                setError(err.response?.data?.message || t('tablesPage.saveError'));
            } finally {
                setSaving(false);
            }
            return;
        }

        if (!name.trim()) {
            setError(t('tablesPage.nameRequired'));
            return;
        }
        setSaving(true);
        try {
            if (editingTable) {
                const res = await updateTable(editingTable.table_id, { name: name.trim() });
                onSaved(res.data, 'updated');
            } else {
                const res = await createTable({ name: name.trim() });
                onSaved(res.data, 'created');
            }
            onHide();
        } catch (err) {
            setError(err.response?.data?.message || t('tablesPage.saveError'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {editingTable ? t('tablesPage.editTable') : t('tablesPage.newTable')}
                </Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && <Alert variant="danger" className="py-2">{error}</Alert>}

                    {!editingTable && (
                        <div className="mb-3 d-flex gap-4">
                            <Form.Check
                                type="radio"
                                id="table-mode-one"
                                label={t('tablesPage.createOne')}
                                checked={mode === 'one'}
                                onChange={() => setMode('one')}
                            />
                            <Form.Check
                                type="radio"
                                id="table-mode-many"
                                label={t('tablesPage.createMany')}
                                checked={mode === 'many'}
                                onChange={() => setMode('many')}
                            />
                        </div>
                    )}

                    {(editingTable || mode === 'one') && (
                        <Form.Group>
                            <Form.Label>{t('tablesPage.tableName')}</Form.Label>
                            <Form.Control
                                type="text"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                placeholder={t('tablesPage.tableNamePlaceholder')}
                                autoFocus
                            />
                        </Form.Group>
                    )}

                    {!editingTable && mode === 'many' && (
                        <>
                            <ButtonGroup size="sm" className="mb-3">
                                <Button
                                    variant={bulkTab === 'range' ? 'primary' : 'outline-primary'}
                                    onClick={() => setBulkTab('range')}
                                    type="button"
                                >
                                    {t('tablesPage.byRange')}
                                </Button>
                                <Button
                                    variant={bulkTab === 'list' ? 'primary' : 'outline-primary'}
                                    onClick={() => setBulkTab('list')}
                                    type="button"
                                >
                                    {t('tablesPage.byList')}
                                </Button>
                            </ButtonGroup>

                            {bulkTab === 'range' ? (
                                <>
                                    <Form.Group className="mb-2">
                                        <Form.Label>{t('tablesPage.prefix')}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={prefix}
                                            onChange={e => setPrefix(e.target.value)}
                                            placeholder={t('tablesPage.prefixPlaceholder')}
                                        />
                                    </Form.Group>
                                    <div className="d-flex gap-2">
                                        <Form.Group>
                                            <Form.Label>{t('tablesPage.startNumber')}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min={0}
                                                value={startNumber}
                                                onChange={e => setStartNumber(e.target.value)}
                                            />
                                        </Form.Group>
                                        <Form.Group>
                                            <Form.Label>{t('tablesPage.quantity')}</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min={1}
                                                max={100}
                                                value={quantity}
                                                onChange={e => setQuantity(e.target.value)}
                                            />
                                        </Form.Group>
                                    </div>
                                </>
                            ) : (
                                <Form.Group>
                                    <Form.Label>{t('tablesPage.namesListLabel')}</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={6}
                                        value={namesText}
                                        onChange={e => setNamesText(e.target.value)}
                                        placeholder={t('tablesPage.namesListPlaceholder')}
                                    />
                                </Form.Group>
                            )}

                            {bulkNames.length > 0 && (
                                <p className="text-muted small mt-2 mb-0">
                                    {t('tablesPage.bulkPreview', { names: previewNames })}
                                </p>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>{t('cancel')}</Button>
                    <Button variant="primary" type="submit" disabled={saving}>
                        {saving ? <Spinner size="sm" animation="border" className="me-1" /> : null}
                        {t('save', 'Guardar')}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

// ─── QRModal ─────────────────────────────────────────────────────────────────

const QRModal = ({ show, onHide, table, businessSlug }) => {
    const { t } = useTranslation();
    if (!table) return null;

    const catalogUrl = `${window.location.origin}/business/${businessSlug}?table_id=${table.table_id}&table=${encodeURIComponent(table.name)}`;

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>{t('tablesPage.qrTitle', { name: table.name })}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="text-center">
                <p className="text-muted small mb-3">{t('tablesPage.qrHint')}</p>
                <QRCodeCanvas
                    id={`qr-${table.table_id}`}
                    value={catalogUrl}
                    size={220}
                    level="H"
                    includeMargin
                />
                <div className="mt-3">
                    <small className="text-muted text-break">{catalogUrl}</small>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>{t('cancel')}</Button>
                <Button variant="primary" onClick={() => downloadQR(table.table_id, table.name)}>
                    {t('tablesPage.downloadQR')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// ─── TablesManagementPage ─────────────────────────────────────────────────────

const TablesManagementPage = () => {
    const { t } = useTranslation();
    const { business } = useContext(AuthContext);

    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [showFormModal, setShowFormModal] = useState(false);
    const [editingTable, setEditingTable] = useState(null);

    const [showQRModal, setShowQRModal] = useState(false);
    const [qrTable, setQrTable] = useState(null);

    const [showInactive, setShowInactive] = useState(false);

    const fetchTables = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await getTables({ all: 'true' });
            setTables(res.data);
        } catch {
            setError(t('tablesPage.fetchError'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchTables();
    }, [fetchTables]);

    const handleSaved = (savedTable, action) => {
        if (action === 'created') {
            setTables(prev => [...prev, savedTable].sort((a, b) => a.name.localeCompare(b.name)));
            toast.success(t('tablesPage.createSuccess'));
        } else if (action === 'bulkCreated') {
            const { created = [], skipped = [] } = savedTable;
            setTables(prev => [...prev, ...created].sort((a, b) => a.name.localeCompare(b.name)));
            toast.success(t('tablesPage.bulkCreateSuccess', { count: created.length }));
            if (skipped.length) {
                toast(t('tablesPage.bulkSkipped', { names: skipped.map(s => s.name).join(', ') }));
            }
        } else {
            setTables(prev => prev.map(t => t.table_id === savedTable.table_id ? savedTable : t));
            toast.success(t('tablesPage.updateSuccess'));
        }
    };

    const handleDeactivate = async (table) => {
        if (!window.confirm(t('tablesPage.confirmDeactivate', { name: table.name }))) return;
        try {
            await deactivateTable(table.table_id);
            setTables(prev => prev.map(t => t.table_id === table.table_id ? { ...t, is_active: false } : t));
            toast.success(t('tablesPage.deactivateSuccess'));
        } catch (err) {
            toast.error(err.response?.data?.message || t('tablesPage.deactivateError'));
        }
    };

    const handleActivate = async (table) => {
        try {
            const res = await activateTable(table.table_id);
            setTables(prev => prev.map(t => t.table_id === table.table_id ? res.data : t));
            toast.success(t('tablesPage.activateSuccess'));
        } catch (err) {
            toast.error(err.response?.data?.message || t('tablesPage.activateError'));
        }
    };

    const visibleTables = showInactive ? tables : tables.filter(t => t.is_active);

    return (
        <Container className="py-4">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h4 className="mb-0">{t('tablesPage.title')}</h4>
                    <p className="text-muted small mb-0">{t('tablesPage.subtitle')}</p>
                </div>
                <Button
                    variant="primary"
                    onClick={() => { setEditingTable(null); setShowFormModal(true); }}
                >
                    + {t('tablesPage.newTable')}
                </Button>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            <Card>
                <Card.Header className="d-flex align-items-center justify-content-between py-2">
                    <span className="fw-semibold">{t('tablesPage.tableList')}</span>
                    <Form.Check
                        type="switch"
                        id="show-inactive"
                        label={t('tablesPage.showInactive')}
                        checked={showInactive}
                        onChange={e => setShowInactive(e.target.checked)}
                    />
                </Card.Header>
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" />
                        </div>
                    ) : visibleTables.length === 0 ? (
                        <p className="text-muted text-center py-4">{t('tablesPage.emptyState')}</p>
                    ) : (
                        <Table responsive hover className="mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th>{t('tablesPage.tableName')}</th>
                                    <th>{t('tablesPage.status')}</th>
                                    <th className="text-end">{t('tablesPage.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleTables.map(table => (
                                    <tr key={table.table_id}>
                                        <td className="fw-semibold align-middle">{table.name}</td>
                                        <td className="align-middle">
                                            <Badge bg={table.is_active ? 'success' : 'secondary'}>
                                                {table.is_active ? t('tablesPage.active') : t('tablesPage.inactive')}
                                            </Badge>
                                        </td>
                                        <td className="text-end align-middle">
                                            <div className="d-flex gap-2 justify-content-end">
                                                {table.is_active && (
                                                    <>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-secondary"
                                                            onClick={() => { setQrTable(table); setShowQRModal(true); }}
                                                        >
                                                            QR
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-primary"
                                                            onClick={() => { setEditingTable(table); setShowFormModal(true); }}
                                                        >
                                                            {t('edit', 'Editar')}
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-danger"
                                                            onClick={() => handleDeactivate(table)}
                                                        >
                                                            {t('tablesPage.deactivate')}
                                                        </Button>
                                                    </>
                                                )}
                                                {!table.is_active && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline-success"
                                                        onClick={() => handleActivate(table)}
                                                    >
                                                        {t('tablesPage.activate')}
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            <TableFormModal
                show={showFormModal}
                onHide={() => setShowFormModal(false)}
                onSaved={handleSaved}
                editingTable={editingTable}
            />

            <QRModal
                show={showQRModal}
                onHide={() => setShowQRModal(false)}
                table={qrTable}
                businessSlug={business?.slug}
            />
        </Container>
    );
};

export default TablesManagementPage;
